/**
 * The date macro the assistant writes into stored card text and every surface
 * substitutes at read time: `<date=2026-08-18 />`.
 *
 * The third grammar beside the two in {@link ./inlineMarkup.ts}, and the only
 * one that is a substitution rather than a drawn element — no click target,
 * nothing to resolve, just words computed against the reader's clock. Stored
 * text outlives the day it was composed on, so "posted <date=2026-08-18 />"
 * reads "posted today" on the day itself and "posted Aug 18" ever after,
 * without the writer having to choose between warm-but-stale and
 * accurate-but-cold.
 *
 * Lives beside the chip grammars for the reason they share a package: the
 * writer-side guard and every reader-side substitution must agree on one
 * pattern, or a token one side accepts reaches a screen the other never
 * rewrites.
 */

/**
 * Capture groups: 1 = year, 2 = month, 3 = day.
 *
 * Digit-strict where the entity grammar is loose, and the difference is the
 * degrade path. A malformed entity id still renders a chip that says what it
 * is, so its pattern swallows the token; a date has no such fallback — the
 * only alternatives are rendering wrong or rendering raw — so a sloppy payload
 * (`<date=next-week />`) deliberately fails this pattern, stays an unknown tag
 * to `unknownMarkupTagPattern`, and is refused at write time while its author
 * is still around to fix it. The `=` keeps micromark from reading the token as
 * HTML, same as the other grammars.
 *
 * The optional whitespace before the close is horizontal only: a newline
 * inside a token splits it across markdown paragraphs before any substitution
 * runs, so a token the guard accepted would still reach the screen raw.
 *
 * A factory for the same reason as its siblings: a `/g/` regex carries mutable
 * `lastIndex` across call sites.
 */
export const dateMacroPattern = (): RegExp => /<date=(\d{4})-(\d{2})-(\d{2})[ \t]*\/>/g;

/**
 * UTC midnight of the calendar date. `setUTCFullYear` rather than `Date.UTC`,
 * whose legacy remap of years 0–99 to 1900+ would misread a `00xx` payload.
 */
const utcMidnight = (year: number, month: number, day: number): Date => {
  const probe = new Date(0);
  probe.setUTCFullYear(year, month - 1, day);
  return probe;
};

/** Round-trip check: rejects month 13 and day overflows Date would carry over. */
const isRealCalendarDate = (year: number, month: number, day: number): boolean => {
  const probe = utcMidnight(year, month, day);
  return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
};

const MONTH_ABBREVIATIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export interface ICalendarDate {
  year: number;
  month: number;
  day: number;
}

/** The calendar date `instant` falls on in `timeZone` — the reader's "today". */
const calendarDateAt = (instant: Date, timeZone: string): ICalendarDate => {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(instant);
  } catch {
    // An unrecognized zone throws at construction; UTC is the one that cannot.
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(instant);
  }
  const read = (type: 'year' | 'month' | 'day'): number => Number(parts.find((part) => part.type === type)?.value);
  return { year: read('year'), month: read('month'), day: read('day') };
};

const MS_PER_DAY = 86_400_000;

/**
 * The words a reader at `now` in `timeZone` would use for a calendar day:
 * `today`, `tomorrow`, `yesterday`, or the date itself (`Aug 18`, with the
 * year appended when it is not the reader's current one).
 *
 * The day arithmetic compares calendar dates mapped to UTC midnights, never
 * elapsed milliseconds, so a DST transition between the two days cannot skew
 * the count. Expects a real calendar date; {@link renderDateMacros} checks
 * that before it calls.
 */
export function describeCalendarDay(date: ICalendarDate, opts: { now: Date; timeZone: string }): string {
  const today = calendarDateAt(opts.now, opts.timeZone);
  const dayDiff =
    (utcMidnight(date.year, date.month, date.day).getTime() -
      utcMidnight(today.year, today.month, today.day).getTime()) /
    MS_PER_DAY;
  if (dayDiff === 0) {
    return 'today';
  }
  if (dayDiff === 1) {
    return 'tomorrow';
  }
  if (dayDiff === -1) {
    return 'yesterday';
  }
  const monthName = MONTH_ABBREVIATIONS[date.month - 1];
  return date.year === today.year ? `${monthName} ${date.day}` : `${monthName} ${date.day}, ${date.year}`;
}

/**
 * Substitute every date macro in `text` with {@link describeCalendarDay}'s
 * words for that day.
 *
 * A payload that is digit-shaped but no real date (`2026-13-45`) passes
 * through untouched: this function never throws and never guesses — keeping
 * such a token out of storage is the write guard's job.
 */
export function renderDateMacros(text: string, opts: { now: Date; timeZone: string }): string {
  if (!text.includes('<date=')) {
    return text;
  }
  return text.replace(dateMacroPattern(), (token, yearText: string, monthText: string, dayText: string) => {
    const date = { year: Number(yearText), month: Number(monthText), day: Number(dayText) };
    return isRealCalendarDate(date.year, date.month, date.day) ? describeCalendarDay(date, opts) : token;
  });
}

/**
 * Whether `token` is a date macro every surface will substitute — the
 * grammar's shape carrying a real calendar date.
 *
 * The renderable/unrenderable line for this grammar, drawn where
 * `unknownMarkupTagPattern` cannot see it: `<date=2026-02-30 />` matches the
 * digit shape, but {@link renderDateMacros} would leave it on screen as raw
 * markup, so only a token passing the calendar check counts as renderable.
 */
export const isRenderableDateMacro = (token: string): boolean => {
  const [match] = [...token.matchAll(dateMacroPattern())];
  // The whole token must be the macro. A tag merely containing one —
  // `<category_id=<date=2026-08-18 />` — would render with the inner macro
  // substituted and the outer markup still raw on screen.
  return (
    match !== undefined &&
    match.index === 0 &&
    match[0].length === token.length &&
    isRealCalendarDate(Number(match[1]), Number(match[2]), Number(match[3]))
  );
};
